import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Calendar, Check, Pencil, Plus, Trash2, X } from 'lucide-react';
import { cn } from '../../../lib/utils';
import {
  primaryText,
  secondaryText,
  semiBoldText,
  mediumText,
  smallTextSize,
  extraSmallTextSize,
  borderPrimary,
} from '../../../styling/stylingHelper';
import { formatPlainDate } from '../../../utils/dateUtils';
import { useMenuChangeStore } from '../../../stores/appMenuChangeStore';
import RestaurantLogo from '../../ui/RestaurantLogo';
import TaskReminder from './TaskReminder';
import { FilterDropdown, FilterOption } from '../reports/hub/utilization/FilterDropdown';
import {
  MenuChangeTask,
  MenuChangeWeek,
  RolloutDetail,
  WeekStatus,
  TaskUpdateRequest,
  addTask as apiAddTask,
  cancelRollout as apiCancelRollout,
  deleteTask as apiDeleteTask,
  renameWeek as apiRenameWeek,
  updateRollout as apiUpdateRollout,
  updateTask as apiUpdateTask,
} from '../../../services/menuChange/menuChangeService';

interface MenuChangeTimelineProps {
  restaurantId: string;
  restaurantName: string;
  restaurantLogoUrl?: string | null;
  canManage: boolean;
  initialRolloutId?: string;
  initialWeekPos?: number;
  focusTaskId?: string;
}

const WEEK_BAR_COLOR: Record<WeekStatus, string> = {
  done: 'bg-[#18B081]',
  not_done: 'bg-[#F04438]',
  inprogress: 'bg-[#18B081]',
  blocked: 'bg-[#F04438]',
  upcoming: 'bg-[#D9D9D9] dark:bg-[#1E3A52]',
};

const WeekBar: React.FC<{
  week: MenuChangeWeek;
  label: string;
  statusLabel: string;
  selected: boolean;
  onSelect: () => void;
}> = ({ week, label, statusLabel, selected, onSelect }) => (
  <button
    type="button"
    onClick={onSelect}
    title={statusLabel}
    aria-label={`${label}: ${statusLabel}`}
    aria-pressed={selected}
    className={cn(
      'flex flex-col flex-1 min-w-0 text-left rounded-lg p-1.5 transition-colors',
      selected ? 'bg-zinc-100 dark:bg-zinc-800 ring-1 ring-[#0E8965] dark:ring-[#0E8965]' : 'hover:bg-zinc-50 dark:hover:bg-zinc-800/50',
      // Blocked weeks read as actionable (click to resolve).
      (week.status === 'blocked' || week.status === 'not_done') && !selected && 'ring-1 ring-[#F04438] dark:ring-[#F04438]'
    )}
  >
    <div className={cn('h-3 rounded-full w-full', WEEK_BAR_COLOR[week.status])} />
    <span
      className={cn(
        'mt-2 text-[16px] leading-6 font-semibold',
        (week.status === 'blocked' || week.status === 'not_done') ? 'text-[#F04438]' : week.status === 'upcoming' ? 'text-[#98A2B3]' : 'text-[#101828] dark:text-[#94A3B8]'
      )}
    >
      {label}
    </span>
    <span className="text-[12px] leading-[18px] truncate text-[#101828] dark:text-[#94A3B8] opacity-80">{week.label}</span>
  </button>
);

const MenuChangeTimeline: React.FC<MenuChangeTimelineProps> = ({
  restaurantId,
  restaurantName,
  restaurantLogoUrl,
  canManage,
  initialRolloutId,
  initialWeekPos,
  focusTaskId,
}) => {
  const { t } = useTranslation();
  const detail = useMenuChangeStore((s) => s.detailByRestaurant[restaurantId]);
  const loadDetail = useMenuChangeStore((s) => s.loadDetail);
  const reconcileRollout = useMenuChangeStore((s) => s.applyRollout);
  const loadReminders = useMenuChangeStore(s => s.loadReminders);

  const previousActiveId = useRef(detail?.active?.id);
  const [selectedRolloutId, setSelectedRolloutId] = useState<string | null>(initialRolloutId ?? null);
  const [savingTask, setSavingTask] = useState(false);
  const savingTaskRef = useRef(false);
  const [taskError, setTaskError] = useState<string | null>(null);
  const [dateDraft, setDateDraft] = useState<{ taskId: string; value: string } | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [editingGoLive, setEditingGoLive] = useState(false);
  const [goLiveDraft, setGoLiveDraft] = useState('');
  const [addingTaskWeekPos, setAddingTaskWeekPos] = useState<number | null>(null);
  const [newTaskLabel, setNewTaskLabel] = useState('');
  const [editingWeekPos, setEditingWeekPos] = useState<number | null>(null);
  const [weekLabelDraft, setWeekLabelDraft] = useState('');
  // Which week's task list is shown. null → follow the current/first-open week.
  const [selectedWeekPos, setSelectedWeekPos] = useState<number | null>(initialWeekPos ?? null);

  useEffect(() => {
    loadDetail(restaurantId);
  }, [restaurantId, loadDetail]);

  // Starting a newer menu change should open it, while finishing the current
  // one keeps its historical task view selected for corrections.
  useEffect(() => {
    if (!initialRolloutId && detail?.active?.id && detail.active.id !== previousActiveId.current) {
      setSelectedRolloutId(null);
      setSelectedWeekPos(null);
      setDateDraft(null);
    }
    previousActiveId.current = detail?.active?.id;
  }, [detail?.active?.id, initialRolloutId]);

  // Reset the manual week selection when switching restaurants.
  useEffect(() => {
    setSelectedWeekPos(initialWeekPos ?? null);
    setSelectedRolloutId(initialRolloutId ?? null);
    setTaskError(null);
    setDateDraft(null);
  }, [restaurantId, initialRolloutId, initialWeekPos]);

  const rollout = selectedRolloutId
    ? [detail?.active, ...(detail?.history ?? [])].find((item) => item?.id === selectedRolloutId)
    : detail?.active ?? detail?.history[0];
  const canEditContent = canManage && rollout?.status === 'active';
  const canEditTasks = canManage && rollout?.status !== 'cancelled';
  const assignableUsers = detail?.assignableUsers;

  useEffect(() => {
    if (rollout && canEditTasks) void loadReminders(rollout.id, true);
  }, [rollout, canEditTasks, loadReminders]);

  // Built once per render, not per task row: a week can hold many tasks.
  // `name` already falls back to the email server-side — don't print it twice.
  const assigneeOptions: FilterOption[] = useMemo(
    () => [
      { value: '', label: t('menuChange.unassigned') },
      ...(assignableUsers ?? []).map((u) => ({
        value: u.id,
        label: u.name,
        sublabel: u.name === u.email ? undefined : u.email,
      })),
    ],
    [assignableUsers, t]
  );

  const statusLabel = (status: WeekStatus): string => t(`menuChange.weekStatus.${status}`);

  // Apply a fresh rollout detail returned by any mutating endpoint.
  const applyRollout = (updated: RolloutDetail) => {
    reconcileRollout(restaurantId, updated);
    setSelectedRolloutId(updated.id);
  };

  // Default week = the current one, or the first non-done week if not started /
  // between weeks. An explicit selection (any week bar) overrides it.
  const defaultWeek = useMemo(
    () => rollout?.weeks.find((w) => w.pos === rollout.currentWeekPos)
      ?? rollout?.weeks.find((w) => w.status !== 'done')
      ?? rollout?.weeks[0],
    [rollout]
  );
  const selectedWeek = useMemo(
    () => (selectedWeekPos != null ? rollout?.weeks.find((w) => w.pos === selectedWeekPos) : undefined) ?? defaultWeek,
    [rollout, selectedWeekPos, defaultWeek]
  );

  if (!rollout) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center gap-2">
        <Calendar className="w-10 h-10 text-zinc-300 dark:text-zinc-600" />
        <p className={cn(smallTextSize, secondaryText)}>{t('menuChange.noActiveRollout')}</p>
      </div>
    );
  }

  const handleTaskUpdate = async (task: MenuChangeTask, body: TaskUpdateRequest) => {
    if (savingTaskRef.current) return;
    savingTaskRef.current = true;
    setSavingTask(true);
    setTaskError(null);
    try {
      const updated = await apiUpdateTask(rollout.id, task.taskId, body);
      applyRollout(updated);
      if (body.completedOn) setDateDraft(null);
    } catch (error) {
      console.error('Failed to update menu change task:', error);
      setTaskError(t('menuChange.taskSaveFailed'));
    } finally {
      savingTaskRef.current = false;
      setSavingTask(false);
    }
  };

  const handleAddTask = async (pos: number) => {
    if (!newTaskLabel.trim()) return;
    try {
      const updated = await apiAddTask(rollout.id, pos, { label: newTaskLabel.trim() });
      applyRollout(updated);
      setNewTaskLabel('');
      setAddingTaskWeekPos(null);
    } catch (error) {
      console.error('Failed to add task:', error);
    }
  };

  const handleDeleteTask = async (task: MenuChangeTask) => {
    try {
      const updated = await apiDeleteTask(rollout.id, task.taskId);
      applyRollout(updated);
    } catch (error) {
      console.error('Failed to delete task:', error);
    }
  };

  const handleRenameWeek = async (pos: number) => {
    if (!weekLabelDraft.trim()) return;
    try {
      const updated = await apiRenameWeek(rollout.id, pos, weekLabelDraft.trim());
      applyRollout(updated);
      setEditingWeekPos(null);
    } catch (error) {
      console.error('Failed to rename week:', error);
    }
  };

  const handleSaveGoLive = async () => {
    if (!goLiveDraft) return;
    try {
      const updated = await apiUpdateRollout(rollout.id, { goLiveDate: goLiveDraft });
      applyRollout(updated);
      setEditingGoLive(false);
    } catch (error) {
      console.error('Failed to update go-live date:', error);
    }
  };

  const handleCancel = async () => {
    if (!detail) return;
    try {
      const cancelled = await apiCancelRollout(rollout.id);
      setShowCancelConfirm(false);
      applyRollout(cancelled);
    } catch (error) {
      console.error('Failed to cancel rollout:', error);
    }
  };

  const renderTask = (task: MenuChangeTask, locked: boolean) => {
    const unassigned = !task.assigneeUserId;
    const cannotAnswer = locked && task.status === 'open';
    const editingDate = dateDraft?.taskId === task.taskId;
    return (
      <div key={task.taskId} id={`menu-task-${task.taskId}`} className={cn('flex items-center gap-3 py-2', focusTaskId === task.taskId && 'rounded-lg ring-2 ring-textGreen dark:ring-textGreen-dark')}>
        <button
          onClick={() => handleTaskUpdate(task, { status: task.status === 'done' ? 'open' : 'done' })}
          disabled={!canEditTasks || savingTask || cannotAnswer}
          title={cannotAnswer ? t('menuChange.lockedWeekHint') : undefined}
          className={cn(
            'w-5 h-5 rounded-md flex-shrink-0 border flex items-center justify-center transition-colors',
            task.status === 'done' ? 'bg-[#0E8965] dark:bg-[#0E8965] border-[#0E8965] dark:border-[#0E8965]' : 'border-zinc-300 dark:border-zinc-600',
            cannotAnswer && 'opacity-40 cursor-not-allowed'
          )}
          aria-label={t('menuChange.toggleTask')}
        >
          {task.status === 'done' && <Check className="w-3 h-3 text-white dark:text-white" strokeWidth={3} />}
        </button>

        <div className="min-w-0 flex-1">
          <p
            className={cn(
              smallTextSize,
              mediumText,
              task.status === 'done' ? cn('line-through', secondaryText) : primaryText
            )}
          >
            {task.label}
          </p>
          <div className="flex items-center gap-2 mt-0.5">
            {canEditContent ? (
              <FilterDropdown
                compact
                searchable
                className="min-w-0"
                // Falls back to the server-resolved name if the assignee is no
                // longer in the assignable list (staff removed from the restaurant).
                label={task.assigneeName ?? t('menuChange.unassigned')}
                searchPlaceholder="menuChange.searchAssignee"
                options={assigneeOptions}
                value={task.assigneeUserId ?? ''}
                onChange={(value) => handleTaskUpdate(task, { assigneeUserId: value || null })}
                triggerClassName={cn(
                  extraSmallTextSize,
                  mediumText,
                  unassigned ? 'text-[#B42318] dark:text-textRed-dark' : secondaryText
                )}
              />
            ) : (
              <span className={cn(extraSmallTextSize, unassigned ? 'text-[#B42318] dark:text-textRed-dark' : secondaryText)}>
                {task.assigneeName ?? t('menuChange.unassigned')}
              </span>
            )}
            {unassigned && <AlertTriangle className="w-3 h-3 text-[#B42318] dark:text-textRed-dark" />}
          </div>
          {canEditTasks && task.status !== 'done' && <TaskReminder rolloutId={rollout.id} taskId={task.taskId} />}
          {task.status === 'done' && (
            <div className="flex items-center gap-2 mt-0.5">
              {editingDate ? (
                <form className="flex items-center gap-2" onSubmit={(event) => {
                  event.preventDefault();
                  if (dateDraft.value) void handleTaskUpdate(task, { completedOn: dateDraft.value });
                }}>
                  <input type="date" required value={dateDraft.value}
                    aria-label={t('menuChange.completionDate')}
                    onChange={(event) => setDateDraft({ taskId: task.taskId, value: event.target.value })}
                    disabled={savingTask}
                    className={cn(extraSmallTextSize, 'px-2 py-1 rounded-lg border bg-white dark:bg-background-dark', borderPrimary, primaryText)} />
                  <button type="submit" disabled={savingTask || !dateDraft.value} className="text-[#0E8965] dark:text-[#0E8965]" aria-label={t('common.save')}>
                    <Check className="w-4 h-4" />
                  </button>
                  <button type="button" disabled={savingTask} onClick={() => setDateDraft(null)} className={secondaryText} aria-label={t('common.cancel')}>
                    <X className="w-4 h-4" />
                  </button>
                </form>
              ) : (
                <>
                  <span className={cn(extraSmallTextSize, secondaryText)}>
                    {task.completedOn
                      ? t('menuChange.completedOn', { date: formatPlainDate(task.completedOn) })
                      : t('menuChange.completionDateUnknown')}
                  </span>
                  {canEditTasks && (
                    <button
                      type="button"
                      disabled={savingTask}
                      onClick={() => { setDateDraft({ taskId: task.taskId, value: task.completedOn ?? '' }); setTaskError(null); }}
                      aria-label={t('menuChange.editCompletionDate', { task: task.label })}
                      className={cn(secondaryText, 'hover:text-[#0E8965] dark:hover:text-[#0E8965]')}
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {/* Not done = an explicit answer: unlocks later weeks but keeps this week red. */}
        {canEditTasks && task.status !== 'done' && (
          <button
            onClick={() => handleTaskUpdate(task, { status: task.status === 'not_done' ? 'open' : 'not_done' })}
            disabled={savingTask || cannotAnswer}
            title={cannotAnswer ? t('menuChange.lockedWeekHint') : undefined}
            aria-pressed={task.status === 'not_done'}
            className={cn(
              extraSmallTextSize,
              mediumText,
              'px-2 py-0.5 rounded-full border whitespace-nowrap flex-shrink-0 transition-colors',
              task.status === 'not_done'
                ? 'bg-[#FEF3F2] dark:bg-red-500/10 border-[#F04438] dark:border-[#F04438] text-[#F04438] dark:text-textRed-dark'
                : cn(borderPrimary, secondaryText, 'hover:text-[#F04438] dark:hover:text-textRed-dark'),
              cannotAnswer && 'opacity-40 cursor-not-allowed'
            )}
          >
            {t('menuChange.taskStatus.not_done')}
          </button>
        )}
        {!canEditTasks && task.status === 'not_done' && (
          <span className={cn(extraSmallTextSize, mediumText, 'px-2 py-0.5 rounded-full whitespace-nowrap flex-shrink-0 bg-[#FEF3F2] dark:bg-red-500/10 text-[#F04438] dark:text-textRed-dark')}>
            {t('menuChange.taskStatus.not_done')}
          </span>
        )}

        {canEditContent && (
          <button
            onClick={() => handleDeleteTask(task)}
            className="text-zinc-400 dark:text-zinc-500 hover:text-red-500 dark:hover:text-red-400 flex-shrink-0"
            aria-label={t('menuChange.removeTask')}
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
    );
  };

  // Rollout-level status pill (mirrors the mock TimelineView StatusBadge).
  // currentWeekPos 0 = week 1's window hasn't opened yet → scheduled, not live.
  const rolloutStatus: 'live' | 'blocked' | 'complete' | 'incomplete' | 'scheduled' | 'cancelled' =
    rollout.status === 'cancelled' ? 'cancelled' : rollout.status === 'complete'
      ? rollout.hasUnfinishedTasks ? 'incomplete' : 'complete'
      : rollout.currentWeekPos === 0
        ? 'scheduled'
        : rollout.weeks.some((w) => w.status === 'blocked')
          ? 'blocked'
          : 'live';
  const rolloutBadge =
    rolloutStatus === 'blocked' || rolloutStatus === 'incomplete' ? 'bg-[#F04438]' : rolloutStatus === 'scheduled' || rolloutStatus === 'cancelled' ? 'bg-[#85888E]' : 'bg-[#18B081]';

  // Sequential completion: week N unlocks only once every earlier task has an answer.
  const selectedWeekLocked = !!selectedWeek
    && rollout.weeks.some((w) => w.pos < selectedWeek.pos && w.tasks.some((tk) => tk.status === 'open'));

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      {taskError && <p role="alert" className={cn(smallTextSize, 'text-[#B42318] dark:text-textRed-dark')}>{taskError}</p>}
      {/* Restaurant header + status badge */}
      <div className="flex items-start sm:items-center sm:flex-row flex-col justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <RestaurantLogo logoUrl={restaurantLogoUrl} name={restaurantName} size="medium" />
          <div className="min-w-0">
            <p className={cn('text-[24px] leading-[38px] font-bold truncate', primaryText)}>{restaurantName}</p>
            <p className="text-[16px] leading-[24px] font-medium text-[#0C111D] dark:text-[#94A3B8]">
              {t('menuChange.launchDate')}: {formatPlainDate(rollout.goLiveDate)}
            </p>
          </div>
        </div>
        <span className={cn('inline-flex items-center gap-1.5 px-3 h-9 rounded-full text-[16px] font-semibold text-white dark:text-white', rolloutBadge)}>
          <span className="w-2.5 h-2.5 rounded-full bg-white dark:bg-white inline-block" />
          {t(`menuChange.status.${rolloutStatus}`)}
        </span>
      </div>

      {/* Go-live date control (admin-editable) + overall progress bar */}
      <div className={cn('rounded-xl border p-4', borderPrimary)}>
        <div className="flex items-center justify-between gap-3 mb-3">
          {editingGoLive ? (
            <div className="flex items-center gap-2 flex-wrap">
              <Calendar className="w-5 h-5 text-zinc-400 dark:text-zinc-500 flex-shrink-0" />
              <input
                type="date"
                value={goLiveDraft}
                onChange={(e) => setGoLiveDraft(e.target.value)}
                autoFocus
                className={cn(smallTextSize, 'px-2 py-1 rounded-lg border', borderPrimary, primaryText, 'bg-transparent dark:bg-transparent')}
              />
              <button onClick={handleSaveGoLive} className="text-[#0E8965] dark:text-[#0E8965]" aria-label={t('common.save')}>
                <Check className="w-5 h-5" />
              </button>
              <button onClick={() => setEditingGoLive(false)} className="text-zinc-400 dark:text-zinc-500" aria-label={t('common.cancel')}>
                <X className="w-5 h-5" />
              </button>
            </div>
          ) : canEditContent ? (
            // The whole date is the edit affordance — no separate tiny "Edit" link.
            <button
              type="button"
              onClick={() => {
                setGoLiveDraft(rollout.goLiveDate);
                setEditingGoLive(true);
              }}
              aria-label={t('menuChange.editGoLive')}
              title={t('menuChange.editGoLive')}
              className="group flex items-center gap-2 min-w-0 -m-1.5 p-1.5 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors"
            >
              <Calendar className="w-5 h-5 text-zinc-400 dark:text-zinc-500 flex-shrink-0" />
              <span className={cn('text-[16px] sm:text-[18px] leading-7 font-semibold truncate', primaryText)}>
                {t('menuChange.goLive')}: {formatPlainDate(rollout.goLiveDate)}
              </span>
              <Pencil className="w-4 h-4 flex-shrink-0 text-[#0E8965] dark:text-[#0E8965] opacity-60 group-hover:opacity-100 transition-opacity" />
            </button>
          ) : (
            <div className="flex items-center gap-2 min-w-0">
              <Calendar className="w-5 h-5 text-zinc-400 dark:text-zinc-500 flex-shrink-0" />
              <span className={cn('text-[16px] sm:text-[18px] leading-7 font-semibold truncate', primaryText)}>
                {t('menuChange.goLive')}: {formatPlainDate(rollout.goLiveDate)}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end mb-1">
          <span className={cn(extraSmallTextSize, mediumText, primaryText)}>{rollout.progress}%</span>
        </div>
        <div className="w-full h-2 bg-[#EAECF0] dark:bg-[#1E3A52] rounded-full overflow-hidden">
          <div className="h-full bg-[#0E8965] dark:bg-[#0E8965] rounded-full transition-all" style={{ width: `${Math.min(rollout.progress, 100)}%` }} />
        </div>
      </div>

      {/* Week timeline bars */}
      <div className="rounded-2xl border border-transparent dark:border-borderPrimary-dark bg-[#F9FAFB] dark:bg-[#0A1F30] p-4 sm:p-6">
        <div className="flex max-lg:flex-wrap items-center justify-between gap-4 mb-5">
          <p className={cn('text-[18px] sm:text-[24px] leading-8 font-semibold', primaryText)}>
            {t('menuChange.timelineTitle', { total: rollout.numWeeks })}
          </p>
          <p className="text-[14px] sm:text-[16px] leading-6 font-medium text-[#0C111D] dark:text-[#94A3B8]">
            {rollout.currentWeekPos === 0
              ? t('menuChange.notStartedOn', { date: formatPlainDate(rollout.startDate) })
              : t('menuChange.weekOf', { current: rollout.currentWeekPos, total: rollout.numWeeks })}
          </p>
        </div>
        <div className="grid grid-cols-4 sm:grid-cols-6 xl:grid-cols-8 gap-2 -mx-1.5">
          {rollout.weeks.map((w) => (
            <WeekBar
              key={w.pos}
              week={w}
              label={t('menuChange.weekLabel', { pos: w.pos })}
              statusLabel={statusLabel(w.status)}
              selected={selectedWeek?.pos === w.pos}
              onSelect={() => setSelectedWeekPos(w.pos)}
            />
          ))}
        </div>
      </div>

      {/* Selected week's tasks (defaults to the current week; any week bar is clickable) */}
      {selectedWeek && (
        <div className={cn('rounded-xl border p-4', (selectedWeek.status === 'blocked' || selectedWeek.status === 'not_done') ? 'border-[#F04438] dark:border-[#F04438]' : borderPrimary)}>
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2 min-w-0">
              {editingWeekPos === selectedWeek.pos ? (
                <div className="flex items-center gap-2">
                  <input
                    value={weekLabelDraft}
                    onChange={(e) => setWeekLabelDraft(e.target.value)}
                    className={cn(smallTextSize, 'px-2 py-1 rounded-lg border', borderPrimary, primaryText, 'bg-transparent dark:bg-transparent')}
                    autoFocus
                  />
                  <button onClick={() => handleRenameWeek(selectedWeek.pos)} className="text-[#0E8965] dark:text-[#0E8965]">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={() => setEditingWeekPos(null)} className="text-zinc-400 dark:text-zinc-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <p className={cn(smallTextSize, semiBoldText, primaryText, 'truncate')}>
                  {t('menuChange.weekLabel', { pos: selectedWeek.pos })} — {selectedWeek.label}
                </p>
              )}
            </div>
            <span
              className={cn(
                extraSmallTextSize,
                mediumText,
                'px-2 py-0.5 rounded-full whitespace-nowrap',
                (selectedWeek.status === 'blocked' || selectedWeek.status === 'not_done')
                  ? 'bg-[#FEF3F2] dark:bg-red-500/10 text-[#F04438] dark:text-textRed-dark'
                  : 'bg-[#EDFCF5] dark:bg-green-500/10 text-[#18B081] dark:text-textGreen-dark'
              )}
            >
              {statusLabel(selectedWeek.status)}
            </span>
          </div>

          {selectedWeek.status === 'blocked' && (
            <p className={cn(extraSmallTextSize, mediumText, 'text-[#B42318] dark:text-textRed-dark mb-2')}>
              {t('menuChange.blockedHint')}
            </p>
          )}

          {selectedWeekLocked && (
            <p className={cn(extraSmallTextSize, mediumText, secondaryText, 'mb-2')}>
              {t('menuChange.lockedWeekHint')}
            </p>
          )}

          {canEditContent && editingWeekPos !== selectedWeek.pos && (
            <button
              onClick={() => {
                setWeekLabelDraft(selectedWeek.label);
                setEditingWeekPos(selectedWeek.pos);
              }}
              className={cn(extraSmallTextSize, mediumText, 'text-[#0E8965] dark:text-[#0E8965] mb-2')}
            >
              {t('menuChange.renameWeek')}
            </button>
          )}

          <div className="divide-y divide-zinc-100 dark:divide-zinc-800">
            {selectedWeek.tasks.length === 0 ? (
              <p className={cn(smallTextSize, secondaryText, 'py-2')}>{t('menuChange.noTasksThisWeek')}</p>
            ) : (
              selectedWeek.tasks.map((task) => renderTask(task, selectedWeekLocked))
            )}
          </div>

          {canEditContent && (
            <div className="mt-3">
              {addingTaskWeekPos === selectedWeek.pos ? (
                <div className="flex items-center gap-2">
                  <input
                    value={newTaskLabel}
                    onChange={(e) => setNewTaskLabel(e.target.value)}
                    placeholder={t('menuChange.taskLabelPlaceholder')}
                    className={cn(smallTextSize, 'flex-1 px-2 py-1.5 rounded-lg border', borderPrimary, primaryText, 'bg-transparent dark:bg-transparent outline-none')}
                    autoFocus
                  />
                  <button onClick={() => handleAddTask(selectedWeek.pos)} className="text-[#0E8965] dark:text-[#0E8965]">
                    <Check className="w-4 h-4" />
                  </button>
                  <button onClick={() => setAddingTaskWeekPos(null)} className="text-zinc-400 dark:text-zinc-500">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setAddingTaskWeekPos(selectedWeek.pos)}
                  className={cn(extraSmallTextSize, mediumText, 'flex items-center gap-1 text-[#0E8965] dark:text-[#0E8965]')}
                >
                  <Plus className="w-3.5 h-3.5" />
                  {t('menuChange.addTask')}
                </button>
              )}
            </div>
          )}
        </div>
      )}

      {/* History — click a row to view/correct that menu change; click again to go back. */}
      {detail && detail.history.length > 0 && (
        <div className={cn('rounded-xl border p-4', borderPrimary)}>
          <p className={cn(smallTextSize, semiBoldText, primaryText, 'mb-3')}>{t('menuChange.history')}</p>
          <div className="flex flex-col gap-2">
            {detail.history.map((h) => (
              <button
                key={h.id}
                type="button"
                onClick={() => {
                  setSelectedRolloutId(h.id === rollout.id && detail.active ? null : h.id);
                  setSelectedWeekPos(null);
                  setDateDraft(null);
                  setTaskError(null);
                  setEditingGoLive(false);
                  setEditingWeekPos(null);
                  setAddingTaskWeekPos(null);
                  setShowCancelConfirm(false);
                }}
                className="flex items-center justify-between gap-2 text-left"
              >
                <div className="min-w-0">
                  <p className={cn(smallTextSize, mediumText, h.id === rollout.id ? 'text-[#0E8965] dark:text-[#0E8965]' : primaryText, 'truncate')}>{h.name}</p>
                  <p className={cn(extraSmallTextSize, secondaryText)}>{formatPlainDate(h.goLiveDate)}</p>
                </div>
                <span className={cn(extraSmallTextSize, secondaryText)}>
                  {t(h.status === 'complete' && h.hasUnfinishedTasks ? 'menuChange.status.incomplete' : `menuChange.rolloutStatus.${h.status}`)}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Cancel rollout (account managers) */}
      {canEditContent && (
        <div>
          {showCancelConfirm ? (
            <div className="flex items-center gap-3">
              <span className={cn(smallTextSize, secondaryText)}>{t('menuChange.confirmCancel')}</span>
              <button
                onClick={handleCancel}
                className="px-3 py-1.5 text-sm font-medium text-white dark:text-white bg-red-600 dark:bg-red-600 rounded-lg hover:bg-red-700 dark:hover:bg-red-700"
              >
                {t('menuChange.cancelRollout')}
              </button>
              <button
                onClick={() => setShowCancelConfirm(false)}
                className={cn(smallTextSize, mediumText, secondaryText)}
              >
                {t('common.cancel')}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowCancelConfirm(true)}
              className={cn(smallTextSize, mediumText, 'text-red-600 dark:text-red-400')}
            >
              {t('menuChange.cancelRollout')}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default MenuChangeTimeline;
